import { isAxiosError } from 'axios';

export interface AdminApiError {
  status: number;
  code: string;
  message: string;
  details: unknown | null;
  requestId?: string;
}

export type AdminApiResult<T = unknown> =
  | { ok: true; data: T; auditEventId?: string | null }
  | { ok: false; error: AdminApiError };

export interface AdminWriteResponse<T = unknown> {
  data: T;
  auditEventId: string | null;
}

interface StructuredAdminError {
  code?: unknown;
  message?: unknown;
  details?: unknown;
  requestId?: unknown;
}

interface AdminErrorPayload {
  error?: unknown;
  message?: unknown;
}

const parseAdminErrorPayload = (
  payload: unknown,
  fallbackMessage: string,
  status: number,
): AdminApiError => {
  if (typeof payload === 'string') {
    return {
      status,
      code: 'ADMIN_ERROR',
      message: payload,
      details: null,
    };
  }

  if (typeof payload !== 'object' || payload === null) {
    return {
      status,
      code: 'ADMIN_ERROR',
      message: fallbackMessage,
      details: null,
    };
  }

  const data = payload as AdminErrorPayload;
  if (typeof data.error === 'string') {
    return {
      status,
      code: 'ADMIN_ERROR',
      message: data.error,
      details: null,
    };
  }

  if (typeof data.error === 'object' && data.error !== null) {
    const structured = data.error as StructuredAdminError;
    return {
      status,
      code:
        typeof structured.code === 'string' ? structured.code : 'ADMIN_ERROR',
      message:
        typeof structured.message === 'string'
          ? structured.message
          : fallbackMessage,
      details: structured.details ?? null,
      requestId:
        typeof structured.requestId === 'string'
          ? structured.requestId
          : undefined,
    };
  }

  return {
    status,
    code: 'ADMIN_ERROR',
    message: typeof data.message === 'string' ? data.message : fallbackMessage,
    details: null,
  };
};

export const parseAdminApiError = (
  error: unknown,
  fallbackMessage: string,
): AdminApiError => {
  if (isAxiosError(error)) {
    return parseAdminErrorPayload(
      error.response?.data,
      fallbackMessage,
      error.response?.status ?? 500,
    );
  }

  if (error instanceof Error && error.message) {
    return {
      status: 500,
      code: 'ADMIN_CLIENT_ERROR',
      message: error.message,
      details: null,
    };
  }

  return {
    status: 500,
    code: 'ADMIN_CLIENT_ERROR',
    message: fallbackMessage,
    details: null,
  };
};

export const formatAdminApiError = (
  error: AdminApiError,
  fallbackMessage = 'Admin action failed',
): string => {
  const message = error.message || fallbackMessage;
  return error.requestId
    ? `${message} (Request ID: ${error.requestId})`
    : message;
};

export const toAdminApiResult = async <T>(
  request: () => Promise<{ data: T | AdminWriteResponse<T> }>,
  fallbackMessage: string,
  logError?: (error: unknown) => void,
): Promise<AdminApiResult<T>> => {
  try {
    const response = await request();
    const responseData = response.data;

    if (
      typeof responseData === 'object' &&
      responseData !== null &&
      'data' in responseData &&
      'auditEventId' in responseData
    ) {
      const writeResponse = responseData as AdminWriteResponse<T>;
      return {
        ok: true,
        data: writeResponse.data,
        auditEventId: writeResponse.auditEventId,
      };
    }

    return { ok: true, data: responseData as T };
  } catch (error) {
    logError?.(error);
    return {
      ok: false,
      error: parseAdminApiError(error, fallbackMessage),
    };
  }
};
